import React from 'react';

/**
 * ILLUSTRATIONS, DRAWN RATHER THAN FETCHED.
 *
 * Every hero here is SVG written by hand: a few hundred bytes, crisp at any
 * size, themed from the post's own accent colour, and carrying no request to
 * anybody else's CDN — which matters on a site that just removed two of those
 * from its critical path. A stock photograph of a smiling receptionist would
 * cost 200 KB and say nothing.
 *
 * `aria-hidden` on all of them: each one sits beside a heading that already
 * says what the post is about, so announcing the decoration twice is noise.
 */

const Frame = ({ tint, children }) => (
  <svg viewBox="0 0 320 180" role="presentation" aria-hidden="true" focusable="false" width="100%">
    <defs>
      <linearGradient id={`g-${tint.replace('#', '')}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={tint} stopOpacity="0.18" />
        <stop offset="100%" stopColor={tint} stopOpacity="0.04" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" rx="14" fill={`url(#g-${tint.replace('#', '')})`} />
    {children}
  </svg>
);

/** One full column beside four thin ones: the whole business, not one hour. */
export const ArtAllInOne = ({ tint = '#FE4E00' }) => (
  <Frame tint={tint}>
    <rect x="28" y="40" width="54" height="104" rx="8" fill={tint} opacity="0.9" />
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} x={104 + i * 48} y={96 - i * 6} width="34" height={48 + i * 6} rx="7" fill={tint} opacity="0.26" />
    ))}
    <circle cx="55" cy="26" r="7" fill={tint} />
  </Frame>
);

/** Nights between an arrival and a departure, not a block on a clock. */
export const ArtStay = ({ tint = '#2E7D6B' }) => (
  <Frame tint={tint}>
    <circle cx="58" cy="90" r="11" fill={tint} />
    <circle cx="262" cy="90" r="11" fill={tint} />
    <path d="M58 90 H262" stroke={tint} strokeWidth="3" strokeDasharray="2 10" strokeLinecap="round" opacity="0.6" />
    {[0, 1, 2].map((i) => (
      <rect key={i} x={96 + i * 46} y="66" width="34" height="48" rx="7" fill={tint} opacity="0.3" />
    ))}
  </Frame>
);

/** Four chairs; the third is empty. */
export const ArtEmptyChair = ({ tint = '#C2410C' }) => (
  <Frame tint={tint}>
    {[0, 1, 2, 3].map((i) => (
      <g key={i} transform={`translate(${44 + i * 64}, 52)`}>
        <rect width="44" height="58" rx="9" fill={tint} opacity={i === 2 ? 0.12 : 0.85} />
        {i === 2 ? (
          <path d="M12 20 L32 40 M32 20 L12 40" stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.7" />
        ) : (
          <circle cx="22" cy="26" r="8" fill="#fff" opacity="0.85" />
        )}
      </g>
    ))}
  </Frame>
);

/* ── Sixteen more, 2026-10-15. Same rules: hand-drawn, a few hundred bytes,
 * one idea each, aria-hidden because the heading beside them already says it. */

/** Three bubbles on one side, a ruled record on the other. */
export const ArtChatVsRecord = ({ tint = '#0F766E' }) => (
  <Frame tint={tint}>
    {[0, 1, 2].map((i) => (
      <rect key={i} x={30 + (i % 2) * 14} y={46 + i * 32} width="76" height="24" rx="12" fill={tint} opacity={0.75 - i * 0.18} />
    ))}
    <path d="M160 36 V144" stroke={tint} strokeWidth="2" strokeDasharray="3 7" opacity="0.5" />
    <rect x="190" y="46" width="100" height="88" rx="10" fill={tint} opacity="0.16" />
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} x="202" y={60 + i * 18} width={i === 3 ? 44 : 76} height="7" rx="3.5" fill={tint} opacity="0.75" />
    ))}
  </Frame>
);

/** People who came once, and the one arrow that brings them back. */
export const ArtReturning = ({ tint = '#7C3AED' }) => (
  <Frame tint={tint}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <circle key={i} cx={52 + i * 44} cy="118" r="13" fill={tint} opacity={i < 2 ? 0.85 : 0.2} />
    ))}
    <path d="M254 92 C254 50 96 50 96 92" stroke={tint} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.8" />
    <path d="M89 80 L96 93 L104 80" stroke={tint} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </Frame>
);

/** A phone with the four things a stranger checks. */
export const ArtTrustedPage = ({ tint = '#2563EB' }) => (
  <Frame tint={tint}>
    <rect x="112" y="24" width="96" height="134" rx="14" fill="#fff" opacity="0.9" />
    <rect x="112" y="24" width="96" height="134" rx="14" fill="none" stroke={tint} strokeWidth="2.5" opacity="0.8" />
    <rect x="124" y="38" width="72" height="30" rx="7" fill={tint} opacity="0.28" />
    <rect x="124" y="78" width="44" height="9" rx="4.5" fill={tint} opacity="0.8" />
    <rect x="124" y="94" width="60" height="7" rx="3.5" fill={tint} opacity="0.35" />
    <rect x="124" y="118" width="72" height="22" rx="11" fill={tint} opacity="0.9" />
  </Frame>
);

/** Four and a half, which reads as true where five does not. */
export const ArtStars = ({ tint = '#D97706' }) => {
  const star = 'M16 2 L20 12 L31 12.6 L22.5 19.5 L25.4 30 L16 24 L6.6 30 L9.5 19.5 L1 12.6 L12 12 Z';
  return (
    <Frame tint={tint}>
      <defs>
        <clipPath id="halfStar"><rect x="0" y="0" width="16" height="32" /></clipPath>
      </defs>
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(${44 + i * 52}, 74)`}><path d={star} fill={tint} /></g>
      ))}
      <g transform="translate(252, 74)">
        <path d={star} fill={tint} opacity="0.22" />
        <g clipPath="url(#halfStar)"><path d={star} fill={tint} /></g>
      </g>
    </Frame>
  );
};

/** Four scattered cards, one folder. */
export const ArtOneFolder = ({ tint = '#0E7490' }) => (
  <Frame tint={tint}>
    {[[36, 36, -12], [38, 96, 9], [92, 28, 6], [96, 104, -7]].map(([x, y, r], i) => (
      <rect key={i} x={x} y={y} width="46" height="34" rx="6" fill={tint} opacity="0.26" transform={`rotate(${r} ${x + 23} ${y + 17})`} />
    ))}
    <path d="M176 70 H212" stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.6" />
    <path d="M176 110 H212" stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.6" />
    <path d="M204 62 L214 70 L204 78" stroke={tint} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M204 102 L214 110 L204 118" stroke={tint} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M228 50 h30 l8 10 h26 a8 8 0 0 1 8 8 v62 a8 8 0 0 1 -8 8 h-64 a8 8 0 0 1 -8 -8 v-72 a8 8 0 0 1 8 -8 z" fill={tint} opacity="0.9" />
  </Frame>
);

/** One job, three stages — and a short booking inside the empty middle. */
export const ArtPhases = ({ tint = '#BE185D' }) => (
  <Frame tint={tint}>
    <rect x="30" y="58" width="78" height="36" rx="8" fill={tint} opacity="0.9" />
    <rect x="116" y="58" width="88" height="36" rx="8" fill="none" stroke={tint} strokeWidth="2.5" strokeDasharray="6 6" opacity="0.7" />
    <rect x="212" y="58" width="78" height="36" rx="8" fill={tint} opacity="0.9" />
    <rect x="128" y="108" width="64" height="26" rx="8" fill={tint} opacity="0.45" />
    <path d="M160 100 V106" stroke={tint} strokeWidth="2.5" strokeLinecap="round" opacity="0.8" />
  </Frame>
);

/** Takings, and the bar that did not arrive. */
export const ArtNoShow = ({ tint = '#B91C1C' }) => (
  <Frame tint={tint}>
    <path d="M28 142 H292" stroke={tint} strokeWidth="2" opacity="0.4" />
    {[52, 78, 104, 58, 86].map((h, i) => (
      i === 3 ? (
        <rect key={i} x={40 + i * 50} y={142 - h} width="34" height={h} rx="6" fill="none" stroke={tint} strokeWidth="2.5" strokeDasharray="5 5" opacity="0.8" />
      ) : (
        <rect key={i} x={40 + i * 50} y={142 - h} width="34" height={h} rx="6" fill={tint} opacity="0.85" />
      )
    ))}
  </Frame>
);

/** The shelf behind the chair. */
export const ArtShelf = ({ tint = '#15803D' }) => (
  <Frame tint={tint}>
    {[0, 1].map((row) => (
      <g key={row}>
        <path d={`M44 ${86 + row * 46} H276`} stroke={tint} strokeWidth="3" strokeLinecap="round" opacity="0.6" />
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i} transform={`translate(${62 + i * 44}, ${86 + row * 46})`}>
            <rect x="-9" y={-28 - (i % 3) * 5} width="18" height={28 + (i % 3) * 5} rx="4" fill={tint} opacity={0.35 + (i % 3) * 0.2} />
            <rect x="-4" y={-34 - (i % 3) * 5} width="8" height="7" rx="2" fill={tint} opacity="0.8" />
          </g>
        ))}
      </g>
    ))}
  </Frame>
);

/** A board, and twelve people in a room. */
export const ArtTeaching = ({ tint = '#7E22CE' }) => (
  <Frame tint={tint}>
    <rect x="96" y="24" width="128" height="52" rx="8" fill={tint} opacity="0.85" />
    {[0, 1, 2].map((i) => (
      <rect key={i} x="110" y={38 + i * 13} width={i === 2 ? 56 : 100} height="6" rx="3" fill="#fff" opacity="0.75" />
    ))}
    {[0, 1, 2].map((r) => (
      [0, 1, 2, 3].map((c) => (
        <circle key={`${r}-${c}`} cx={88 + c * 48} cy={104 + r * 24} r="8" fill={tint} opacity={0.65 - r * 0.15} />
      ))
    ))}
  </Frame>
);

/** A record: a photograph, a history, and something signed. */
export const ArtRecord = ({ tint = '#0891B2' }) => (
  <Frame tint={tint}>
    <rect x="52" y="30" width="216" height="120" rx="12" fill="#fff" opacity="0.9" />
    <rect x="52" y="30" width="216" height="120" rx="12" fill="none" stroke={tint} strokeWidth="2.5" opacity="0.75" />
    <rect x="68" y="46" width="62" height="62" rx="8" fill={tint} opacity="0.3" />
    <circle cx="88" cy="66" r="7" fill={tint} opacity="0.7" />
    <path d="M70 100 L90 80 L108 98 L118 90 L128 100 Z" fill={tint} opacity="0.65" />
    {[0, 1, 2].map((i) => (
      <rect key={i} x="146" y={52 + i * 18} width={i === 2 ? 60 : 104} height="7" rx="3.5" fill={tint} opacity="0.55" />
    ))}
    <circle cx="160" cy="124" r="11" fill={tint} opacity="0.9" />
    <path d="M155 124 L159 128 L166 120" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M182 128 c10 -12 18 4 28 -6 s18 2 26 -6" stroke={tint} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.7" />
  </Frame>
);

/** Three columns out of one diary. */
export const ArtColumns = ({ tint = '#4F46E5' }) => (
  <Frame tint={tint}>
    {[0, 1, 2].map((c) => (
      <g key={c}>
        <rect x={48 + c * 78} y="30" width="58" height="120" rx="9" fill={tint} opacity="0.12" />
        <rect x={48 + c * 78} y="30" width="58" height="16" rx="8" fill={tint} opacity="0.55" />
      </g>
    ))}
    {[[0, 56, 30], [0, 96, 22], [1, 60, 46], [2, 52, 26], [2, 92, 34]].map(([c, y, h], i) => (
      <rect key={i} x={56 + c * 78} y={y} width="42" height={h} rx="6" fill={tint} opacity="0.85" />
    ))}
  </Frame>
);

/** Money that arrives before the person does. */
export const ArtDeposit = ({ tint = '#047857' }) => (
  <Frame tint={tint}>
    <rect x="36" y="62" width="84" height="56" rx="9" fill={tint} opacity="0.9" />
    <rect x="36" y="78" width="84" height="11" fill="#fff" opacity="0.65" />
    <path d="M136 90 C166 50 206 50 232 84" stroke={tint} strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray="4 8" opacity="0.75" />
    <circle cx="140" cy="92" r="9" fill={tint} opacity="0.8" />
    <circle cx="178" cy="62" r="9" fill={tint} opacity="0.55" />
    <circle cx="214" cy="70" r="9" fill={tint} opacity="0.35" />
    <rect x="232" y="78" width="54" height="66" rx="9" fill={tint} opacity="0.3" />
    <rect x="244" y="62" width="30" height="20" rx="6" fill={tint} opacity="0.9" />
  </Frame>
);

/** One receipt, however the money arrived. */
export const ArtTill = ({ tint = '#A16207' }) => (
  <Frame tint={tint}>
    <path d="M110 24 h100 v116 l-14 -8 -14 8 -14 -8 -14 8 -14 -8 -14 8 -16 -8 z" fill="#fff" opacity="0.92" />
    <path d="M110 24 h100 v116 l-14 -8 -14 8 -14 -8 -14 8 -14 -8 -14 8 -16 -8 z" fill="none" stroke={tint} strokeWidth="2.5" opacity="0.8" />
    {[0, 1, 2].map((i) => (
      <g key={i}>
        <rect x="124" y={44 + i * 20} width={52 - i * 8} height="7" rx="3.5" fill={tint} opacity="0.6" />
        <rect x="178" y={44 + i * 20} width="20" height="7" rx="3.5" fill={tint} opacity="0.4" />
      </g>
    ))}
    <rect x="124" y="108" width="74" height="9" rx="4.5" fill={tint} opacity="0.9" />
    <rect x="38" y="76" width="50" height="34" rx="6" fill={tint} opacity="0.7" />
    <circle cx="258" cy="80" r="15" fill={tint} opacity="0.65" />
    <circle cx="276" cy="80" r="15" fill={tint} opacity="0.4" />
  </Frame>
);

/** A lot of attention, a few bookings, and the number that matters. */
export const ArtFunnel = ({ tint = '#DB2777' }) => (
  <Frame tint={tint}>
    <path d="M42 36 H278 L206 96 V140 L114 140 V96 Z" fill={tint} opacity="0.18" />
    <path d="M42 36 H278 L206 96 V140 L114 140 V96 Z" fill="none" stroke={tint} strokeWidth="2.5" opacity="0.6" />
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <circle key={i} cx={66 + i * 38} cy="54" r="7" fill={tint} opacity="0.75" />
    ))}
    {[0, 1].map((i) => (
      <circle key={i} cx={142 + i * 36} cy="120" r="9" fill={tint} opacity="0.95" />
    ))}
  </Frame>
);

/** Three chairs working and the people waiting for one. */
export const ArtQueue = ({ tint = '#1D4ED8' }) => (
  <Frame tint={tint}>
    {[0, 1, 2].map((i) => (
      <g key={i} transform={`translate(${44 + i * 56}, 48)`}>
        <rect width="42" height="56" rx="9" fill={tint} opacity="0.9" />
        <circle cx="21" cy="22" r="9" fill="#fff" opacity="0.85" />
      </g>
    ))}
    <path d="M228 40 V144" stroke={tint} strokeWidth="2" strokeDasharray="3 7" opacity="0.45" />
    {[0, 1, 2, 3].map((i) => (
      <circle key={i} cx="262" cy={52 + i * 28} r="11" fill={tint} opacity={0.75 - i * 0.14} />
    ))}
  </Frame>
);

/** A month that already knows which squares earned. */
export const ArtCalendarHidden = ({ tint = '#9333EA' }) => (
  <Frame tint={tint}>
    <rect x="40" y="28" width="240" height="124" rx="11" fill="#fff" opacity="0.88" />
    <rect x="40" y="28" width="240" height="124" rx="11" fill="none" stroke={tint} strokeWidth="2.5" opacity="0.7" />
    <rect x="40" y="28" width="240" height="20" rx="11" fill={tint} opacity="0.55" />
    {[0, 1, 2, 3].map((r) => (
      [0, 1, 2, 3, 4, 5].map((c) => {
        const strong = (r === 1 && c === 3) || (r === 2 && c === 3) || (r === 0 && c === 3) || (r === 3 && c === 1);
        return <rect key={`${r}-${c}`} x={54 + c * 37} y={58 + r * 23} width="27" height="16" rx="4" fill={tint} opacity={strong ? 0.9 : 0.16} />;
      })
    ))}
  </Frame>
);

/** One wide bed-block carrying the rest of the business stacked on it. */
export const ArtPropertyStack = ({ tint = '#0F766E' }) => (
  <Frame tint={tint}>
    <rect x="34" y="96" width="252" height="48" rx="10" fill={tint} opacity="0.85" />
    <rect x="48" y="108" width="54" height="24" rx="6" fill="#fff" opacity="0.8" />
    {[0, 1, 2, 3].map((i) => (
      <rect key={i} x={46 + i * 62} y={34 + (i % 2) * 16} width="52" height={48 - (i % 2) * 14} rx="8" fill={tint} opacity={0.42 - i * 0.07} />
    ))}
    <path d="M160 84 V94" stroke={tint} strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
  </Frame>
);

const BY_SLUG = {
  'all-in-one-vs-appointment-apps': ArtAllInOne,
  'run-a-property-on-one-system': ArtStay,
  'deposits-and-the-empty-chair': ArtEmptyChair,
  'why-whatsapp-is-not-enough': ArtChatVsRecord,
  'the-customers-you-already-have': ArtReturning,
  'a-page-people-trust': ArtTrustedPage,
  'what-reviews-are-really-for': ArtStars,
  'a-dental-practice-in-one-place': ArtOneFolder,
  'a-treatment-that-takes-three-visits': ArtPhases,
  'the-cost-of-a-no-show': ArtNoShow,
  'selling-products-beside-the-chair': ArtShelf,
  'teaching-what-you-know': ArtTeaching,
  'a-clinic-that-keeps-its-own-records': ArtRecord,
  'two-chairs-one-diary': ArtColumns,
  'getting-paid-before-they-arrive': ArtDeposit,
  'the-desk-is-a-till-too': ArtTill,
  'ads-that-pay-for-themselves': ArtFunnel,
  'a-barber-shop-at-full-capacity': ArtQueue,
  'what-your-calendar-is-hiding': ArtCalendarHidden,
  'software-for-a-property-business': ArtPropertyStack,
};

/** The hero for a post, or a neutral one for a post that has not got its own. */
export default function BlogArt({ slug }) {
  const Art = BY_SLUG[slug] || ArtAllInOne;
  return <Art />;
}
